import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    inject,
    input,
    signal,
} from '@angular/core';
import {
    ItemDocumentsFacade,
    ItemFacade,
} from '@pf/platform-item-catalog/domain';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { of, switchMap, tap } from 'rxjs';
import { DrawerService, IconModule } from '@pf/shared-ui';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import {
    CpwItemInfoDrawerComponent,
    CpwItemInfoDrawerData,
} from './cpw-item-info-drawer.component';

@Component({
    selector: 'cpw-item-image',
    imports: [IconModule, NzSkeletonModule],
    template: `
        @if (imageUrl() === undefined) {
            <nz-skeleton-element
                nzType="image"
                [nzActive]="true"
                class="cpw-item-image__skeleton"
                [style.width.px]="width()"
                [style.height.px]="height()"></nz-skeleton-element>
        } @else if (imageUrl(); as url) {
            <img
                class="cpw-item-image__thumb"
                [class.cpw-item-image__thumb--interactive]="interactive()"
                [src]="url"
                [width]="width()"
                [height]="height()"
                [alt]="alt()"
                [attr.role]="interactive() ? 'button' : null"
                [attr.tabindex]="interactive() ? 0 : null"
                [attr.title]="interactive() ? 'Tap for item info' : null"
                (click)="onActivate($event)"
                (keydown.enter)="onActivate($event)"
                (keydown.space)="onActivate($event)" />
        } @else if (interactive()) {
            <button
                type="button"
                class="cpw-item-image__placeholder"
                [style.width.px]="width()"
                [style.height.px]="height()"
                title="Tap for item info"
                aria-label="Item details"
                (click)="onActivate($event)">
                <pf-icon iconType="item"></pf-icon>
            </button>
        }
    `,
    styles: [
        `
            :host {
                display: block;
            }

            .cpw-item-image__thumb {
                object-fit: cover;
                border-radius: 4px;
            }

            .cpw-item-image__thumb--interactive {
                cursor: pointer;
                transition: box-shadow 0.2s ease;
            }

            .cpw-item-image__thumb--interactive:hover {
                box-shadow: 0 1px 6px rgba(0, 0, 0, 0.25);
            }

            .cpw-item-image__skeleton {
                display: block;

                ::ng-deep .ant-skeleton-image {
                    width: 100% !important;
                    height: 100% !important;
                    border-radius: 4px;
                }

                // Default placeholder glyph is ~48px and overflows a small
                // thumbnail; scale it down so it's subtle.
                ::ng-deep .ant-skeleton-image-svg {
                    width: 40% !important;
                    height: 40% !important;
                }
            }

            .cpw-item-image__placeholder {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                padding: 0;
                border: 1px dashed var(--pf-border-default, rgba(0, 0, 0, 0.18));
                border-radius: 4px;
                background: var(--pf-surface-elevated, #f5f5f5);
                color: var(--pf-text-secondary, rgba(0, 0, 0, 0.45));
                cursor: pointer;
                transition: box-shadow 0.2s ease;
            }

            .cpw-item-image__placeholder:hover {
                box-shadow: 0 1px 6px rgba(0, 0, 0, 0.2);
            }
        `,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CpwItemImageComponent {
    private readonly itemFacade = inject(ItemFacade);
    private readonly itemDocumentsFacade = inject(ItemDocumentsFacade);
    private readonly drawerService = inject(DrawerService);
    private readonly destroyRef = inject(DestroyRef);

    itemId = input.required<string | undefined>();
    width = input(75);
    height = input(75);
    /** When true (default), tapping opens the item info drawer. */
    interactive = input(true);
    alt = signal('Item image');

    /** undefined = loading, null = item has no image, string = image URL. */
    readonly imageUrl = signal<string | null | undefined>(undefined);

    private readonly isMobile = /Android|iPhone|iPad|iPod/i.test(
        navigator.userAgent
    );

    constructor() {
        toObservable(this.itemId)
            .pipe(
                tap(() => this.imageUrl.set(undefined)),
                switchMap(id =>
                    id ? this.itemFacade.getDefaultItemImage$(id) : of(null)
                ),
                tap(() => {
                    const id = this.itemId();
                    if (id) {
                        this.alt.set(
                            this.itemFacade.getById(id)?.name || 'Item image'
                        );
                    }
                }),
                switchMap(file => {
                    const id = this.itemId();
                    return id && file?.id
                        ? this.itemDocumentsFacade.previewUrl$(id, file.id)
                        : of(null);
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(url => this.imageUrl.set(url ?? null));
    }

    onActivate(event: Event): void {
        if (!this.interactive()) {
            return;
        }
        // Don't let the tap bubble into surrounding scan/task handlers.
        event.stopPropagation();
        event.preventDefault();
        const itemId = this.itemId();
        if (!itemId) {
            return;
        }
        this.drawerService.createTemplateDrawer({
            title: 'Item details',
            content: CpwItemInfoDrawerComponent,
            data: { itemId } as CpwItemInfoDrawerData,
            placement: 'bottom',
            height: '85dvh',
            isMobile: this.isMobile,
        });
    }
}
